import React, { useState, useEffect } from 'react';
import { ErrorDebugger } from '../Debug/ErrorDebugger';
import NotificationTester from './NotificationTester';
import GlobalDeadlineAdmin from './GlobalDeadlineAdmin';
import MilestoneTemplateAdmin from './MilestoneTemplateAdmin';
import MilestoneGenerationAdmin from './MilestoneGenerationAdmin';
import CompletionRuleAdmin from './CompletionRuleAdmin';
import CompletionRuleDiagnostic from '../Debug/CompletionRuleDiagnostic';
import TimelineBuilder from './TimelineBuilder';
import EnhancedInterpolationTestPanel from './EnhancedInterpolationTestPanel';
import APIUsageDashboard from './APIUsageDashboard';
import CrisisMonitor from './CrisisMonitor';
import UserQuotaManagement from './UserQuotaManagement';
import TaskReorderAdmin from './TaskReorderAdmin';
import ProfileTaskSyncAdmin from './ProfileTaskSyncAdmin';
import EssayTaskSyncAdmin from './EssayTaskSyncAdmin';
import EssayManagementAdmin from './EssayManagementAdmin';
import CollegeRequirementsAdmin from './CollegeRequirementsAdmin';
import RecommendationWorkflowGenerator from './RecommendationWorkflowGenerator';
import TaskIntegrityDashboard from './TaskIntegrityDashboard';
import SchemaExplorer from './SchemaExplorer';
import IncompatibilityBanner from './IncompatibilityBanner';
import DebugTaskQuery from './DebugTaskQuery';
import WorkflowVerificationPanel from './WorkflowVerificationPanel';
import EssayAPITester from './EssayAPITester';
// import DebugPanel from '../College/DebugPanel'; // Archived
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabase } from '../../lib/supabase';
import { UserQuotaManager } from '../../services/api/UserQuotaManager';
import {
  BarChart3,
  AlertTriangle,
  Users,
  Activity,
  DollarSign,
  Settings,
  Shield,
  List,
  Calendar,
  TestTube,
  Sliders,
  Key,
  FileText,
  Target,
  ChevronDown, 
  Eye
} from 'lucide-react';

// Essay Access Control Component
const EssayAccessControl = () => {
  const { familyUsers } = useFamilyContext();
  const [essayAccess, setEssayAccess] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load essay access permissions from database
  useEffect(() => {
    loadEssayAccessFromDatabase();
  }, [familyUsers]);

  const loadEssayAccessFromDatabase = async () => {
    if (!familyUsers?.length) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const children = familyUsers.filter(user => user.type !== 'parent');
      const userIds = children.map(child => child.id).filter(Boolean);
      
      if (userIds.length === 0) {
        setEssayAccess({});
        setLoading(false);
        return;
      }

      const { data: accessSettings, error: fetchError } = await supabase
        .from('college_access_settings')
        .select('child_id, has_essay_access')
        .in('child_id', userIds);

      if (fetchError) {
        throw fetchError;
      }

      // Convert array to object for easier access
      const accessMap = {};
      accessSettings?.forEach(setting => {
        accessMap[setting.child_id] = setting.has_essay_access;
      });
      
      setEssayAccess(accessMap);
      console.log('Essay access loaded from database:', accessMap);
      
    } catch (err) {
      console.error('Error loading essay access from database:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Save essay access permissions to database
  const saveEssayAccessToDatabase = async (userId, hasAccess) => {
    try {
      const { error: upsertError } = await supabase
        .from('college_access_settings')
        .upsert({
          child_id: userId,
          has_essay_access: hasAccess,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'child_id'
        });

      if (upsertError) {
        throw upsertError;
      }

      // Update local state
      setEssayAccess(prev => ({
        ...prev,
        [userId]: hasAccess
      }));

      console.log(`Essay access updated for user ${userId}:`, hasAccess);
      
    } catch (err) {
      console.error('Error saving essay access to database:', err);
      setError(err.message);
      throw err; // Re-throw to handle in calling function
    }
  };

  // Toggle access for a family member
  const toggleAccess = async (userId) => {
    const currentAccess = essayAccess[userId] || false;
    const newAccess = !currentAccess;
    
    try {
      await saveEssayAccessToDatabase(userId, newAccess);
    } catch (err) {
      // Error already logged in saveEssayAccessToDatabase
      alert('Failed to update essay access. Please try again.');
    }
  };

  // Enable access for all family members
  const enableAllAccess = async () => {
    const children = familyUsers?.filter(user => user.type !== 'parent') || [];
    
    try {
      for (const child of children) {
        await saveEssayAccessToDatabase(child.id, true);
      }
    } catch (err) {
      alert('Failed to enable access for all users. Some updates may have succeeded.');
    }
  };

  // Disable access for all family members
  const disableAllAccess = async () => {
    const children = familyUsers?.filter(user => user.type !== 'parent') || [];
    
    try {
      for (const child of children) {
        await saveEssayAccessToDatabase(child.id, false);
      }
    } catch (err) {
      alert('Failed to disable access for all users. Some updates may have succeeded.');
    }
  };

  const children = familyUsers?.filter(user => user.type !== 'parent') || [];

  // Quick enable for Charlie (test user)
  const enableCharlieOnly = async () => {
    const charlie = children.find(child => 
      (child.name || child.displayName)?.toLowerCase().includes('charlie')
    );
    if (charlie) {
      try {
        // Disable everyone else
        for (const child of children) {
          if (child.id !== charlie.id) {
            await saveEssayAccessToDatabase(child.id, false);
          }
        }
        // Enable Charlie only
        await saveEssayAccessToDatabase(charlie.id, true);
      } catch (err) {
        alert('Failed to set Charlie-only access. Some updates may have succeeded.');
      }
    }
  };

  return (
    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-sm">
      <div className="font-semibold mb-3 text-orange-800">Essay Module Access Control</div>
      <p className="text-orange-700 mb-4 text-xs">
        Control which family members can access the Essay Development Hub. Settings are stored in the database.
      </p>
      
      {loading ? (
        <div className="text-orange-600 text-xs">Loading access settings...</div>
      ) : error ? (
        <div className="text-red-600 text-xs">Error: {error}</div>
      ) : children.length === 0 ? (
        <div className="text-orange-600 text-xs">No children found in family context.</div>
      ) : (
        <div className="space-y-3">
          {/* Individual Controls */}
          <div className="space-y-2">
            {children.map(child => (
              <div key={child.id} className="flex items-center justify-between bg-white rounded p-2">
                <span className="font-medium text-slate-800">
                  {child.name || child.displayName}
                </span>
                <button
                  onClick={() => toggleAccess(child.id)}
                  disabled={loading}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    loading ? 'bg-stone-300 text-stone-500 cursor-not-allowed' :
                    essayAccess[child.id] 
                      ? 'bg-emerald-600 text-white hover:bg-emerald-700' 
                      : 'bg-red-100 text-red-700 hover:bg-red-200'
                  }`}
                >
                  {loading ? 'Loading...' : essayAccess[child.id] ? '✓ Access Enabled' : '✗ Access Disabled'}
                </button>
              </div>
            ))}
          </div>

          {/* Bulk Actions */}
          <div className="space-y-2 pt-2 border-t border-orange-200">
            <div className="flex gap-2">
              <button
                onClick={enableAllAccess}
                disabled={loading}
                className={`flex-1 px-3 py-1 rounded text-xs font-medium transition-colors ${
                  loading ? 'bg-stone-300 text-stone-500 cursor-not-allowed' : 'bg-emerald-600 text-white hover:bg-emerald-700'
                }`}
              >
                {loading ? 'Loading...' : 'Enable All'}
              </button>
              <button
                onClick={disableAllAccess}
                disabled={loading}
                className={`flex-1 px-3 py-1 rounded text-xs font-medium transition-colors ${
                  loading ? 'bg-stone-300 text-stone-500 cursor-not-allowed' : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {loading ? 'Loading...' : 'Disable All'}
              </button>
            </div>
            {children.some(child => (child.name || child.displayName)?.toLowerCase().includes('charlie')) && (
              <button
                onClick={enableCharlieOnly}
                disabled={loading}
                className={`w-full px-3 py-1 rounded text-xs font-medium transition-colors ${
                  loading ? 'bg-stone-300 text-stone-500 cursor-not-allowed' : 'bg-azure-600 text-white hover:bg-azure-700'
                }`}
              >
                {loading ? 'Loading...' : '🧪 Test Mode: Charlie Only'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Comparison Hub Access Control Component
const ComparisonHubAccessControl = () => {
  const { familyUsers } = useFamilyContext();
  const [comparisonAccess, setComparisonAccess] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load comparison hub access permissions from database
  useEffect(() => {
    loadComparisonAccessFromDatabase();
  }, [familyUsers]);

  const loadComparisonAccessFromDatabase = async () => {
    if (!familyUsers?.length) return;

    setLoading(true);
    setError(null);

    try {
      const children = familyUsers.filter(user => user.type !== 'parent');
      const userIds = children.map(child => child.id).filter(Boolean);

      if (userIds.length === 0) {
        setComparisonAccess({});
        setLoading(false);
        return;
      }

      const { data: accessSettings, error: fetchError } = await supabase
        .from('college_access_settings')
        .select('child_id, has_comparison_hub_access')
        .in('child_id', userIds);

      if (fetchError) {
        throw fetchError;
      }

      // Convert array to object for easier access
      const accessMap = {};
      accessSettings?.forEach(setting => {
        accessMap[setting.child_id] = setting.has_comparison_hub_access;
      });

      setComparisonAccess(accessMap);
      console.log('Comparison Hub access loaded from database:', accessMap);

    } catch (err) {
      console.error('Error loading comparison hub access from database:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Save comparison hub access permissions to database
  const saveComparisonAccessToDatabase = async (userId, hasAccess) => {
    try {
      const { error: upsertError } = await supabase
        .from('college_access_settings')
        .upsert({
          child_id: userId,
          has_comparison_hub_access: hasAccess,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'child_id'
        });

      if (upsertError) {
        throw upsertError;
      }

      // Update local state
      setComparisonAccess(prev => ({
        ...prev,
        [userId]: hasAccess
      }));

      console.log(`Comparison Hub access updated for user ${userId}:`, hasAccess);

    } catch (err) {
      console.error('Error saving comparison hub access to database:', err);
      setError(err.message);
      throw err; // Re-throw to handle in calling function
    }
  };

  // Toggle access for a family member
  const toggleAccess = async (userId) => {
    const currentAccess = comparisonAccess[userId] || false;
    const newAccess = !currentAccess;

    try {
      await saveComparisonAccessToDatabase(userId, newAccess);
    } catch (err) {
      // Error already logged in saveComparisonAccessToDatabase
      alert('Failed to update comparison hub access. Please try again.');
    }
  };

  // Enable access for all family members
  const enableAllAccess = async () => {
    const children = familyUsers?.filter(user => user.type !== 'parent') || [];

    try {
      for (const child of children) {
        await saveComparisonAccessToDatabase(child.id, true);
      }
    } catch (err) {
      alert('Failed to enable access for all users. Some updates may have succeeded.');
    }
  };

  // Disable access for all family members
  const disableAllAccess = async () => {
    const children = familyUsers?.filter(user => user.type !== 'parent') || [];

    try {
      for (const child of children) {
        await saveComparisonAccessToDatabase(child.id, false);
      }
    } catch (err) {
      alert('Failed to disable access for all users. Some updates may have succeeded.');
    }
  };

  const children = familyUsers?.filter(user => user.type !== 'parent') || [];

  return (
    <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 text-sm">
      <div className="font-semibold mb-3 text-teal-800">Comparison Hub Access Control</div>
      <p className="text-teal-700 mb-4 text-xs">
        Control which family members can access the College Comparison Hub. Settings are stored in the database.
      </p>

      {loading ? (
        <div className="text-teal-600 text-xs">Loading access settings...</div>
      ) : error ? (
        <div className="text-red-600 text-xs">Error: {error}</div>
      ) : children.length === 0 ? (
        <div className="text-teal-600 text-xs">No children found in family context.</div>
      ) : (
        <div className="space-y-3">
          {/* Individual Controls */}
          <div className="space-y-2">
            {children.map(child => (
              <div key={child.id} className="flex items-center justify-between bg-white rounded p-2">
                <span className="font-medium text-slate-800">
                  {child.name || child.displayName}
                </span>
                <button
                  onClick={() => toggleAccess(child.id)}
                  disabled={loading}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    comparisonAccess[child.id]
                      ? 'bg-green-500 text-white hover:bg-green-600'
                      : 'bg-stone-300 text-stone-700 hover:bg-stone-400'
                  } ${loading ? 'cursor-not-allowed opacity-50' : ''}`}
                >
                  {comparisonAccess[child.id] ? 'Enabled' : 'Disabled'}
                </button>
              </div>
            ))}
          </div>

          {/* Bulk Controls */}
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <button
                onClick={enableAllAccess}
                disabled={loading}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                  loading ? 'bg-stone-300 text-stone-500 cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {loading ? 'Loading...' : 'Enable All'}
              </button>
              <button
                onClick={disableAllAccess}
                disabled={loading}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                  loading ? 'bg-stone-300 text-stone-500 cursor-not-allowed' : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {loading ? 'Loading...' : 'Disable All'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Intelligence System Stats Component
const IntelligenceSystemStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const quotaManager = new UserQuotaManager();

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const analytics = await quotaManager.getSystemWideAnalytics();
      setStats({
        apiCallsToday:
          analytics.totalRequests.opus.daily +
          analytics.totalRequests.sonnet.daily +
          analytics.totalRequests.haiku.daily,
        costToday: analytics.dailyCost,
        crisisInterventions: analytics.byCategory.crisis?.daily || 0,
        essayReviews: analytics.byCategory.essay?.daily || 0,
        quotaExhausted: analytics.quotaExhausted.opus + analytics.quotaExhausted.sonnet,
        activeUsers: analytics.totalUsers
      });
      setLoading(false);
    } catch (error) {
      console.error('[Admin] Failed to load intelligence stats:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-20 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      <div className="bg-violet-50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-1">
          <Activity className="w-4 h-4 text-violet-600" />
          <span className="text-xs text-violet-600 font-medium">API Calls</span>
        </div>
        <div className="text-2xl font-bold text-violet-900">{stats?.apiCallsToday || 0}</div>
        <div className="text-xs text-violet-700">Today</div>
      </div>

      <div className="bg-green-50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-1">
          <DollarSign className="w-4 h-4 text-green-600" />
          <span className="text-xs text-green-600 font-medium">Cost</span>
        </div>
        <div className="text-2xl font-bold text-green-900">${stats?.costToday || '0.00'}</div>
        <div className="text-xs text-green-700">Today</div>
      </div>

      <div className="bg-orange-50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle className="w-4 h-4 text-orange-600" />
          <span className="text-xs text-orange-600 font-medium">Crisis</span>
        </div>
        <div className="text-2xl font-bold text-orange-900">{stats?.crisisInterventions || 0}</div>
        <div className="text-xs text-orange-700">Interventions</div>
      </div>

      <div className="bg-blue-50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-1">
          <BarChart3 className="w-4 h-4 text-blue-600" />
          <span className="text-xs text-blue-600 font-medium">Essays</span>
        </div>
        <div className="text-2xl font-bold text-blue-900">{stats?.essayReviews || 0}</div>
        <div className="text-xs text-blue-700">Reviews</div>
      </div>

      <div className="bg-red-50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-1">
          <Users className="w-4 h-4 text-red-600" />
          <span className="text-xs text-red-600 font-medium">At Limit</span>
        </div>
        <div className="text-2xl font-bold text-red-900">{stats?.quotaExhausted || 0}</div>
        <div className="text-xs text-red-700">Users</div>
      </div>

      <div className="bg-indigo-50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-1">
          <Shield className="w-4 h-4 text-indigo-600" />
          <span className="text-xs text-indigo-600 font-medium">Active</span>
        </div>
        <div className="text-2xl font-bold text-indigo-900">{stats?.activeUsers || 0}</div>
        <div className="text-xs text-indigo-700">Users</div>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { familyUsers } = useFamilyContext();

  const toggleGroup = (groupName) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  // Navigation structure
  const navGroups = [
    {
      id: 'timeline',
      name: 'Timeline & Workflows',
      icon: Calendar,
      items: [
        { id: 'workflow-verification', label: 'Workflow Verification', icon: null },
        { id: 'milestones', label: 'Milestone Templates', icon: null },
        { id: 'generation', label: 'Milestone Generator', icon: null },
        { id: 'builder', label: 'Timeline Builder', icon: null },
        { id: 'completion-rules', label: 'Completion Rules', icon: null },
      ]
    },
    {
      id: 'college-journey',
      name: 'College Journey',
      icon: FileText,
      items: [
        { id: 'college-requirements', label: 'College Requirements Admin', icon: Target },
        { id: 'essay-management', label: 'Essay Management', icon: null },
        { id: 'recommendation-generator', label: 'Recommendation Generator', icon: null },
        { id: 'essay-access', label: 'Essay Module Access', icon: null },
        { id: 'comparison-access', label: 'Comparison Hub Access', icon: null },
      ]
    },
    {
      id: 'testing',
      name: 'Testing & Integrity',
      icon: TestTube,
      items: [
        { id: 'essay-api-test', label: 'Essay API Tester', icon: null },
        { id: 'test-profile-wizard', label: 'Test Profile Wizard', icon: null },
        { id: 'task-integrity', label: 'Task Integrity', icon: Shield },
        { id: 'task-reorder', label: 'Task Reorder', icon: List },
        { id: 'completion-diagnostic', label: 'Completion Diagnostic', icon: null },
        { id: 'interpolation-test', label: 'Interpolation Test', icon: null },
        { id: 'profile-sync', label: 'Profile Task Sync', icon: null },
        { id: 'essay-sync', label: 'Essay Task Sync', icon: null },
        { id: 'debug-query', label: 'Debug Task Query', icon: null },
      ]
    },
    {
      id: 'system',
      name: 'System & Config',
      icon: Sliders,
      items: [
        { id: 'api-usage', label: 'API Usage', icon: BarChart3 },
        { id: 'crisis-monitor', label: 'Crisis Monitor', icon: AlertTriangle },
        { id: 'quota-management', label: 'User Quotas', icon: Users },
        { id: 'deadlines', label: 'Global Deadlines', icon: null },
        { id: 'schema-explorer', label: 'Schema Explorer', icon: null },
      ]
    },
    {
      id: 'access',
      name: 'System Tools',
      icon: Settings,
      items: [
        { id: 'overview', label: 'Intelligence Dashboard', icon: null },
        { id: 'notifications', label: 'Notification Testing', icon: null },
        { id: 'error-debug', label: 'Error Debugging', icon: null },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-2xl font-bold text-stone-900">Admin Dashboard</h1>
          <p className="text-sm text-stone-600 mt-1">
            Administrative tools and debugging utilities
          </p>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6">
          {/* Sidebar Navigation */}
          <div className={`flex-shrink-0 transition-all duration-300 ${sidebarCollapsed ? 'w-12' : 'w-64'}`}>
            <div className="sticky top-24 bg-white rounded-lg border border-slate-200 shadow-sm p-3">
              {/* Collapse/Expand Button */}
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="mb-3 w-full flex items-center justify-center p-2 text-xs font-medium text-slate-600 hover:bg-slate-50 rounded-md transition-colors border border-slate-200"
                title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform ${sidebarCollapsed ? 'rotate-90' : '-rotate-90'}`} />
                {!sidebarCollapsed && <span className="ml-2">Collapse</span>}
              </button>

              <nav className={`space-y-2 ${sidebarCollapsed ? 'hidden' : ''}`}>
              {navGroups.map((group, groupIndex) => {
                const GroupIcon = group.icon;
                const isCollapsed = collapsedGroups[group.id];

                return (
                  <div key={group.id} className={groupIndex > 0 ? 'pt-2 border-t border-slate-200' : ''}>
                    {/* Group Header */}
                    <button
                      onClick={() => toggleGroup(group.id)}
                      className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                      <div className="flex items-center gap-2.5">
                        <GroupIcon className="w-4 h-4 text-slate-700" />
                        <span>{group.name}</span>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                    </button>

                    {/* Group Items */}
                    {!isCollapsed && (
                      <div className="mt-1 space-y-0.5 ml-1">
                        {group.items.map((item) => {
                          const ItemIcon = item.icon;
                          const isActive = activeTab === item.id;

                          return (
                            <button
                              key={item.id}
                              onClick={() => setActiveTab(item.id)}
                              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-md transition-all ${
                                isActive
                                  ? 'bg-azure-600 text-white font-medium shadow-sm'
                                  : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                              }`}
                            >
                              {ItemIcon && <ItemIcon className="w-4 h-4" />}
                              <span>{item.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              </nav>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 min-h-screen">
              {activeTab === 'workflow-verification' && (
                <WorkflowVerificationPanel />
              )}

              {activeTab === 'deadlines' && (
                <GlobalDeadlineAdmin />
              )}

              {activeTab === 'milestones' && (
                <MilestoneTemplateAdmin />
              )}

              {activeTab === 'generation' && (
                <MilestoneGenerationAdmin />
              )}

              {activeTab === 'college-requirements' && (
                <CollegeRequirementsAdmin />
              )}

              {activeTab === 'essay-management' && (
                <EssayManagementAdmin />
              )}

              {activeTab === 'recommendation-generator' && (
                <RecommendationWorkflowGenerator />
              )}

              {activeTab === 'essay-access' && (
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-slate-900 mb-4">
                    Essay Module Access Control
                  </h2>
                  <p className="text-slate-600 mb-6">
                    Control which family members can access the Essay Development Hub. Perfect for testing with specific students before full rollout.
                  </p>
                  <EssayAccessControl />
                </div>
              )}

              {activeTab === 'comparison-access' && (
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-slate-900 mb-4">
                    Comparison Hub Access Control
                  </h2>
                  <p className="text-slate-600 mb-6">
                    Control which family members can access the College Comparison Hub.
                  </p>
                  <ComparisonHubAccessControl />
                </div>
              )}

              {activeTab === 'test-profile-wizard' && (
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-slate-900 mb-4">
                    Test Profile Wizard
                  </h2>
                  <p className="text-slate-600 mb-6">
                    Create realistic test student profiles with AI-generated parent insights, assessment questions, and answers. Perfect for testing the entire college journey flow.
                  </p>
                  <div className="bg-gradient-to-br from-azure-50 to-violet-50 rounded-lg p-6 border-2 border-azure-200">
                    <div className="flex items-start gap-4">
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900 mb-2">Complete Profile Generation</h3>
                        <p className="text-sm text-slate-600 mb-4">
                          7-step wizard that guides you through creating a comprehensive test profile including academic data, parent observations, strategic context, questions, and mock answers.
                        </p>
                        <ul className="text-sm text-slate-600 space-y-1 mb-4">
                          <li>• Quick academic profile input with all fields</li>
                          <li>• AI-generated realistic parent insights</li>
                          <li>• Strategic context and personality profiling</li>
                          <li>• Personalized assessment questions (1-15)</li>
                          <li>• Mock student answers with review/edit capability</li>
                          <li>• Export complete profile as JSON</li>
                        </ul>
                      </div>
                    </div>
                    <button
                      onClick={() => window.location.href = '/college/test-profile-wizard'}
                      className="w-full px-6 py-3 bg-azure-600 text-white rounded-lg hover:bg-azure-700 transition-colors font-semibold flex items-center justify-center gap-2"
                    >
                      <span>🚀</span>
                      Launch Test Profile Wizard
                    </button>
                  </div>
                </div>
              )}

              {activeTab === 'notifications' && (
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-slate-900 mb-4">
                    Notification Testing
                  </h2>
                  <p className="text-slate-600 mb-6">
                    Test push notifications to family members across all their devices.
                  </p>
                  <NotificationTester />
                </div>
              )}

              {activeTab === 'error-debug' && (
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-slate-900 mb-4">
                    Error Debugging
                  </h2>
                  <p className="text-slate-600 mb-6">
                    View application errors, auth state, and environment information for troubleshooting.
                  </p>
                  <ErrorDebugger />
                </div>
              )}

              {activeTab === 'completion-rules' && (
                <div className="space-y-4">
                <div className="bg-white rounded-lg shadow-sm border border-stone-200 p-6">
                <h2 className="text-xl font-semibold text-stone-900 mb-4">
                  🤖 Task Completion Rules
                </h2>
                <p className="text-stone-600 mb-6">
                  Manually trigger evaluation of completion rules to auto-complete tasks when conditions are met (e.g., "finalize reach schools" when 4+ reach schools are selected).
                </p>
                <div className="bg-stone-50 rounded-lg p-4">
                  <CompletionRuleAdmin />
                </div>
              </div>
              </div>
              )}

              {activeTab === 'builder' && (
                <>
                  <IncompatibilityBanner
                    title="⚠️ UPDATE REQUIRED - Hierarchical Task Support"
                    severity="warning"
                    status="Functional for flat tasks only"
                    issues={[
                      "This tool loads from milestone_templates, not timeline_items (doesn't show generated tasks)",
                      "Built before hierarchical workflows (parent + subtasks) were implemented",
                      "Cannot display parent tasks with step_count badges (e.g., '6/6 steps')",
                      "Cannot show subtasks nested under parents",
                      "Doesn't query is_subtask, parent_task_id, or step_count fields",
                      "Drag-drop doesn't maintain parent-child relationships",
                      "Use for template design only, not for viewing student timelines"
                    ]}
                  />
                  <TimelineBuilder />
                </>
              )}

              {activeTab === 'task-integrity' && (
                <TaskIntegrityDashboard />
              )}

              {activeTab === 'essay-api-test' && (
                <EssayAPITester />
              )}

              {activeTab === 'interpolation-test' && (
                <EnhancedInterpolationTestPanel />
              )}

              {activeTab === 'completion-diagnostic' && (
                <div className="space-y-4">
                  <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                    <h2 className="text-lg font-semibold text-slate-900 mb-4">
                      🔍 Completion Rule Diagnostic
                    </h2>
                    <p className="text-sm text-slate-600 mb-6">
                      Debug completion rule issues and see exactly why tasks aren't auto-completing.
                      This tool evaluates each task's completion rule against current user data and shows why rules pass or fail.
                    </p>
                    <CompletionRuleDiagnostic />
                  </div>
                </div>
              )}

              {activeTab === 'profile-sync' && (
                <div className="space-y-4">
                  <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                    <h2 className="text-lg font-semibold text-slate-900 mb-4">
                      📋 Profile Task Synchronization
                    </h2>
                    <p className="text-sm text-slate-600 mb-6">
                      Manually trigger profile task generation and synchronization. Tasks are automatically synced when profile fields are updated, but you can force a sync here for testing or recovery.
                    </p>
                    <ProfileTaskSyncAdmin />
                  </div>
                </div>
              )}

              {activeTab === 'essay-sync' && (
                <div className="space-y-4">
                  <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                    <h2 className="text-lg font-semibold text-slate-900 mb-4">
                      ✍️ Essay Task Synchronization
                    </h2>
                    <p className="text-sm text-slate-600 mb-6">
                      Manually trigger essay task generation and synchronization. Tasks are automatically synced when essays are created or updated, but you can force a sync here for testing or recovery.
                    </p>
                    <EssayTaskSyncAdmin />
                  </div>
                </div>
              )}

              {activeTab === 'debug-query' && (
                <div className="space-y-4">
                  <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                    <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      🐛 Debug Task Query
                    </h2>
                    <p className="text-sm text-slate-600 mb-6">
                      Query and inspect all tasks for a user grouped by source_type. Useful for debugging task generation, seeing what tasks were created, and identifying missing tasks.
                    </p>
                    <DebugTaskQuery />
                  </div>
                </div>
              )}

              {activeTab === 'schema-explorer' && (
                <SchemaExplorer />
              )}

              {/* New Intelligence System Components */}
              {activeTab === 'api-usage' && (
                <APIUsageDashboard />
              )}

              {activeTab === 'crisis-monitor' && (
                <CrisisMonitor />
              )}

              {activeTab === 'quota-management' && (
                <UserQuotaManagement />
              )}

              {activeTab === 'task-reorder' && (
                <div className="space-y-4">
                  {/* User Selection */}
                  <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                    <h2 className="text-lg font-semibold text-slate-900 mb-4">Select Student</h2>
                    <p className="text-sm text-slate-600 mb-4">
                      Choose a student to reorder their tasks
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {familyUsers
                        ?.filter(user => user.type !== 'parent')
                        .map(student => (
                          <button
                            key={student.id}
                            onClick={() => setSelectedUserId(student.id)}
                            className={`px-4 py-3 rounded-lg border-2 transition-all ${
                              selectedUserId === student.id
                                ? 'border-emerald-500 bg-emerald-50 text-emerald-900 font-semibold'
                                : 'border-slate-200 bg-white text-slate-700 hover:border-emerald-300 hover:bg-emerald-50'
                            }`}
                          >
                            {student.name || student.displayName}
                          </button>
                        ))}
                    </div>
                  </div>

                  {/* Task Reorder Interface */}
                  {selectedUserId && (
                    <TaskReorderAdmin userId={selectedUserId} />
                  )}

                  {!selectedUserId && (
                    <div className="bg-slate-50 rounded-lg border-2 border-dashed border-slate-300 p-12 text-center">
                      <List className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                      <p className="text-slate-600 font-medium">Select a student to begin reordering tasks</p>
                      <p className="text-sm text-slate-500 mt-1">Choose a student from the list above</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Intelligence System Stats */}
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900 mb-4">
                      Intelligence Dashboard
                    </h2>
                    <p className="text-slate-600 mb-6">
                      Real-time metrics for AI-powered crisis intervention and scheduling optimization.
                    </p>
                    <IntelligenceSystemStats />
                  </div>

                  {/* System Information */}
                  <div className="pt-6 border-t border-slate-200">
                    <h2 className="text-xl font-semibold text-slate-900 mb-4">
                      System Information
                    </h2>
                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                      <div className="bg-slate-50 p-4 rounded-lg">
                        <strong className="text-slate-900">Environment:</strong>
                        <div className="mt-1 text-slate-600">
                          {window.location.hostname === 'localhost' ? 'Development' : 'Production'}
                        </div>
                      </div>
                      <div className="bg-slate-50 p-4 rounded-lg">
                        <strong className="text-slate-900">Build Date:</strong>
                        <div className="mt-1 text-slate-600">
                          {new Date().toLocaleDateString()}
                        </div>
                      </div>
                      <div className="bg-slate-50 p-4 rounded-lg">
                        <strong className="text-slate-900">User Agent:</strong>
                        <div className="mt-1 text-slate-600 text-xs">
                          {navigator.userAgent.slice(0, 60)}...
                        </div>
                      </div>
                      <div className="bg-slate-50 p-4 rounded-lg">
                        <strong className="text-slate-900">Current URL:</strong>
                        <div className="mt-1 text-slate-600 text-xs break-all">
                          {window.location.href}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
